import { type Dispatch, type SetStateAction, useCallback } from 'react';

import SlackIcon from '@/assets/icons/SlackIcon';
import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import usePopupStore from '@/stores/pure/PopupStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import { IntegrationItemStyle } from '../../Integrations/IntergrationStyles';
import slackAskBotService from './service';
import { getSlackAskBotConnectionUrl } from './SlackConnectionUtil';
import type { SlackAskBotInfoT } from './types';

type Props = {
  info?: SlackAskBotInfoT;
  setInfos?: Dispatch<SetStateAction<SlackAskBotInfoT[]>>;
  deleteWorkspace?: (id: string) => void;
};

const DESC_DEFAULT = 'Slack Chatbot';

const SlackConnectionItem = (props: Props) => {
  const color = useColor();
  const { currWorkspace } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
  }));
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));

  const { info, setInfos, deleteWorkspace } = props;

  const handleClick = () => {
    if (!currWorkspace) return;
    const url = getSlackAskBotConnectionUrl(currWorkspace.alias);
    window.open(url);
  };

  const changeStatus = useCallback(async () => {
    if (!currWorkspace) return;
    if (!info) return;
    await slackAskBotService.changeSlackAskBotStatus(info.id);
    setInfos &&
      setInfos((prev: SlackAskBotInfoT[]) =>
        prev.map((item: SlackAskBotInfoT) => {
          if (item.id === info.id) {
            return { ...item, is_active: !item.is_active };
          }
          return item;
        }),
      );
  }, []);

  const handleDisconnectClick = useCallback(() => {
    if (!currWorkspace) return;

    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Are you sure you want to disconnect?',
      confirmText: 'Disconnect',
      onConfirm: () => {
        if (!info) return;
        deleteWorkspace && deleteWorkspace(info.id);
      },
    });
  }, [currWorkspace, setPopup]);

  return (
    <IntegrationItemStyle
      onClick={info ? () => {} : handleClick}
      isIntegrated={false}
      a="center"
      j="space-between"
    >
      <Flex
        a="center"
        g="16px"
        style={{
          opacity: info ? (info.is_active ? 1 : 0.5) : 1,
        }}
      >
        <Flex className="logo-area">
          <SlackIcon />
          <Typo type="label2" weight="sb" color={color.gray800}>
            Slack
          </Typo>
        </Flex>
        <div className="desc-area">
          <Typo type="body2" color={color.gray800}>
            {info === undefined ? DESC_DEFAULT : info.team_name}
          </Typo>
        </div>
      </Flex>
      {info && (
        <Flex g="8px">
          <Button
            onClick={changeStatus}
            variant={info.is_active ? 'error_outlined' : 'outlined'}
            tooltip={
              info.is_active
                ? 'If you deactivate this connection, you will no longer receive messages from Hey Syncly.'
                : 'Activating this connection will deactivate connections in other workspaces.'
            }
          >
            {info.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button variant="error_outlined" onClick={handleDisconnectClick}>
            Disconnect
          </Button>
        </Flex>
      )}
    </IntegrationItemStyle>
  );
};

export default SlackConnectionItem;
